import { useState, useEffect, useRef } from 'react';
import Button from 'react-bootstrap/Button';
import MessagesTable from './MessagesTable'
import { Envelope } from 'react-bootstrap-icons'
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import { OutlinedInputProps, MenuProps } from './muiStyles/muiInput';
import { centerClient } from '../shared/clients';
import { TimeStamp } from './Administration/context/shared';
import { ErrorBoundary } from 'blick.react.utils'
import useReconnectingWebSocket from '../utils/useReconnectingWebSocket';
import ResizableDrawer from '../utils/ResizableDrawer';
import { CloseButton } from 'react-bootstrap';

const LOGS_DIRECTION_LS_KEY = "centerLogsDirection";
type DrawerDirection = 'right' | 'bottom';
export class LogMessage {
    ServiceName: string;
    Time: TimeStamp;
    Message: string;
    LogId: number;
    EntryType: number;
    constructor() {

    }
}
// WebSocket требует JWT (query string ?access_token=), т.к. браузерный WS API не умеет заголовки.
// Фабрика: URL пересобирается перед каждым подключением (актуальный токен).
const buildWsUrl = () => {
    const base = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
    const token = sessionStorage.getItem('access-token');
    return token ? `${base}?access_token=${encodeURIComponent(token)}` : base;
};

interface IncomingMessages {
    Items: LogMessage[];
    LastLogId: number;
}

function MessagesOffCanvas() {
    const [showCanvas, setShowCanvas] = useState(false);
    const [data, setData] = useState<LogMessage[]>([]);
    //const [isLoading, setIsLoading] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [, setLastLogId] = useState(0);
    const [direction, setDirection] = useState<DrawerDirection>(() => {
        const saved = localStorage.getItem(LOGS_DIRECTION_LS_KEY);
        return saved === 'bottom' ? 'bottom' : 'right';
    });
    const canvasRef = useRef<HTMLDivElement>(null);

    const handleDirectionChange = (value: DrawerDirection) => {
        localStorage.setItem(LOGS_DIRECTION_LS_KEY, value);
        setDirection(value);
    };

    const showCanvasRef = useRef(showCanvas);
    useEffect(() => { showCanvasRef.current = showCanvas; }, [showCanvas]);

    const handleClose = () => {
        setShowCanvas(false);
    }
    const handleShow = () => setShowCanvas(true);

    // WebSocket: слушаем входящие сообщения и дополняем data.
    // Авто-reconnect с экспоненциальной задержкой.
    const { isConnected } = useReconnectingWebSocket(buildWsUrl, {
        onMessage: (m) => {
            // попытка извлечь полезную нагрузку
            let payload: unknown = m;
            if (payload && typeof payload === 'object' && 'data' in payload) {
                payload = (payload as { data: unknown }).data;
            }

            if (typeof payload === 'string') {
                try {
                    const parsed = JSON.parse(payload);
                    handleIncomingWsMessage(parsed);
                } catch {
                    // не JSON — игнор
                }
            } else if (typeof payload === 'object' && payload !== null) {
                handleIncomingWsMessage(payload as IncomingMessages);
            }
        },

    });

    const handleIncomingWsMessage = (parsed: IncomingMessages) => {
        if (!parsed) return;

        // Если пришёл пакет сообщений в формате { centerMessages: [...], lastId: number }
        if (Array.isArray(parsed.Items)) {
            const incoming = parsed as IncomingMessages;
            if (typeof incoming.LastLogId === 'number') {
                setLastLogId(incoming.LastLogId);
            }

            if (incoming.Items.length === 0)
                return;

            setData(prev => {
                let newData: LogMessage[] = [...prev, ...incoming.Items];
                // дедупликация по logId, сохраняется последний элемент для каждого id
                newData = [
                    ...new Map(newData.map((item) => [item.LogId, item])).values(),
                ];
                // тримим список, если данных очень много
                if (newData.length > 1000)
                    newData = newData.slice(newData.length - 151);
                return newData;
            });

            // увеличиваем счётчик непрочитанных, только если панель не открыта
            setUnreadCount(prev =>
                prev + (showCanvasRef.current ? 0 : incoming.Items.length));
        }
    };


    useEffect(() => {
        loadAllMessages();
    }, []);

    // после (ре)подключения WS догружаем сообщения, пропущенные за время обрыва;
    // при первом монтировании loadAllMessages уже вызван эффектом выше
    const isConnectedRef = useRef<boolean | null>(null);
    useEffect(() => {
        if (isConnectedRef.current !== null && isConnected)
            loadAllMessages();
        isConnectedRef.current = isConnected;
    }, [isConnected]);

    useEffect(() => {
        if (showCanvas)
            setUnreadCount(0);
    }, [showCanvas]);

    const loadAllMessagesSuccess = (res: IncomingMessages) => {
        setLastLogId(res.LastLogId);
        // merge с уже пришедшими по WS (дедупликация по LogId), а не замена —
        // между REST-ответом и WS-доставкой нет синхронизации
        setData(prev => {
            let merged: LogMessage[] = [...prev, ...res.Items];
            merged = [
                ...new Map(merged.map((item) => [item.LogId, item])).values(),
            ];
            if (merged.length > 1000)
                merged = merged.slice(merged.length - 151);
            return merged;
        });
        if (!showCanvas)
            setUnreadCount((prev) => prev + res.Items.length);
    }

    function loadAllMessages() {
        centerClient.getMessages()
            .then((res) => {
                const data = res.data;
                if (data)
                    loadAllMessagesSuccess(data as unknown as IncomingMessages);
            })
            .catch(() => { });
    }
    return (
        <>
            <Button style={{position:'relative'}} variant="transparent" onClick={handleShow}>
                <Envelope style={{ fill: "var(--text)" }} />
                <label id="unreadMessagesLabel" style={{ display: unreadCount > 0 ? "block" : "none" }}>{unreadCount}</label>
            </Button>
            <ResizableDrawer key={direction} open={showCanvas} handleClose={handleClose} direction={direction}>
                <div ref={canvasRef} style={{ flex: '1 1 auto', minHeight: 0, width: '100%', padding: '0 .5rem', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div id='messagesTableHeader' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', minHeight: '5rem', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 120, marginLeft: 2 }}>
                                <InputLabel sx={{ color: 'var(--text)' }}>Сторона</InputLabel>
                                <Select
                                    value={direction}
                                    label="Сторона"
                                    input={<OutlinedInput sx={OutlinedInputProps} label="Сторона" />}
                                    MenuProps={MenuProps}
                                    onChange={(e) => handleDirectionChange(e.target.value as DrawerDirection)}
                                >
                                    <MenuItem value="right">Справа</MenuItem>
                                    <MenuItem value="bottom">Снизу</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <CloseButton variant="transparent" onClick={handleClose}/>
                    </div>
                    <div id='messagesTable' style={{ flex: 1, minHeight: 0, width: '100%', boxSizing: 'border-box' }}>
                        <ErrorBoundary>
                            <MessagesTable data={data} containerRef={canvasRef} />
                        </ErrorBoundary>
                    </div>
                </div>

            </ResizableDrawer>

        </>
    );
}

export default MessagesOffCanvas;